import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import * as devConfig from "../../../app/configs/devConfig";
import { acceptFailureText } from "../../UserInvite/userInvite.utils";
import { passwordProblem } from "./journey.utils";
import {
  JOURNEY_BUTTON,
  JOURNEY_INPUT,
  JOURNEY_LABEL,
  JourneyError,
  JourneyLayout,
  JourneyTitle,
  LockedEmail,
  PasswordFields,
  StepBars,
} from "./JourneyParts";

type SetupInfo = {
  firstName: string;
  email: string;
  firmName: string;
  expiresAt: string;
};

type PageState =
  | { kind: "loading" }
  | { kind: "problem"; status: number; text: string }
  | { kind: "form"; info: SetupInfo }
  | { kind: "done"; email: string; firmName: string };

const api = () => `${devConfig.getServerBaseUrlForEnv()}/api/account-setup`;

/**
 * New firm owner, from the welcome email: /setup/:token (owner 2026-10-25). Step 1 of 3 = choose the
 * password (the sign-in account is created now); then they sign in and the firm steps follow.
 */
export const AccountSetupPage = () => {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState<PageState>({ kind: "loading" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Set up your account | ProCharted";
    (async () => {
      try {
        const response = await fetch(`${api()}/${encodeURIComponent(token)}`, {
          headers: { Accept: "application/json" },
        });
        if (response.ok)
          setState({
            kind: "form",
            info: (await response.json()) as SetupInfo,
          });
        else
          setState({
            kind: "problem",
            status: response.status,
            text: acceptFailureText(await response.text().catch(() => "")),
          });
      } catch {
        setState({
          kind: "problem",
          status: 0,
          text: "We couldn't reach ProCharted. Check your connection and try again.",
        });
      }
    })();
  }, [token]);

  const problem = passwordProblem(password, confirm);

  const complete = async () => {
    if (state.kind !== "form") return;
    setTried(true);
    if (problem || saving) return;
    setSaving(true);
    setError("");
    try {
      const response = await fetch(`${api()}/complete`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token, password }),
      });
      if (response.ok)
        setState({
          kind: "done",
          email: state.info.email,
          firmName: state.info.firmName,
        });
      else if ([404, 409, 410].includes(response.status))
        setState({
          kind: "problem",
          status: response.status,
          text: acceptFailureText(await response.text()),
        });
      else setError(acceptFailureText(await response.text().catch(() => "")));
    } catch {
      setError(
        "We couldn't reach ProCharted. Check your connection and try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const side = (title: string, text: string) => (
    <>
      <div
        className={"text-[30px] font-extrabold leading-tight lg:text-[36px]"}>
        {title}
      </div>
      <div className={"mt-3.5 max-w-[380px] text-[15px] opacity-90"}>
        {text}
      </div>
    </>
  );

  if (state.kind === "loading")
    return (
      <JourneyLayout side={null}>
        <div className={"text-center text-[#8A90A2]"}>Loading…</div>
      </JourneyLayout>
    );

  if (state.kind === "problem")
    return (
      <JourneyLayout
        side={side("Let's get you in.", "Your firm is waiting for you.")}>
        {state.status === 409 ? (
          <>
            <JourneyTitle title={"You're all set"} text={state.text} />
            <button
              type={"button"}
              className={JOURNEY_BUTTON}
              onClick={() => navigate("/")}>
              Sign in
            </button>
          </>
        ) : (
          <NewLinkForm text={state.text} />
        )}
      </JourneyLayout>
    );

  if (state.kind === "done")
    return (
      <JourneyLayout
        side={side(
          "Nearly there.",
          `Sign in and we'll set up ${state.firmName} together.`,
        )}>
        <div className={"text-center"}>
          <div
            className={
              "mx-auto mb-4 flex h-[76px] w-[76px] items-center justify-center rounded-full bg-[#E3F6EC] text-4xl text-[#1E9E5A]"
            }>
            ✓
          </div>
          <JourneyTitle
            title={"Your password is set"}
            text={
              <>
                Sign in with <b className={"text-[#1F2433]"}>{state.email}</b>{" "}
                and the password you just chose. Then two quick steps about your
                firm.
              </>
            }
          />
          <button
            type={"button"}
            className={JOURNEY_BUTTON}
            onClick={() => navigate("/")}>
            Sign in →
          </button>
        </div>
      </JourneyLayout>
    );

  const info = state.info;
  return (
    <JourneyLayout
      side={side(
        `Welcome aboard, ${info.firmName}.`,
        "Three quick steps and your firm is ready.",
      )}>
      <StepBars total={3} done={1} />
      <JourneyTitle
        step={"Step 1 of 3 · Your password"}
        title={<>Hi {info.firstName || "there"} 👋</>}
        text={"Choose the password you'll use to sign in."}
      />
      <LockedEmail email={info.email} />
      <PasswordFields
        password={password}
        confirm={confirm}
        onPassword={setPassword}
        onConfirm={setConfirm}
        onEnter={() => void complete()}
      />
      <JourneyError text={(tried && problem) || error} />
      <button
        type={"button"}
        className={`${JOURNEY_BUTTON} mt-6`}
        disabled={saving}
        onClick={() => void complete()}>
        {saving ? "Setting up your account…" : "Continue"}
      </button>
    </JourneyLayout>
  );
};

/** Expired / unknown link: ask for a new one by email (same answer whether or not the email exists). */
const NewLinkForm = ({ text }: { text: string }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!email.trim() || sending) return;
    setSending(true);
    try {
      const response = await fetch(`${api()}/resend`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: email.trim() }),
      });
      const body = response.ok
        ? ((await response.json()) as { message?: string })
        : null;
      setSent(
        body?.message ||
          "If that email has a firm waiting to be set up, a new link is on its way.",
      );
    } catch {
      setSent(
        "We couldn't reach ProCharted. Check your connection and try again.",
      );
    } finally {
      setSending(false);
    }
  };
  return (
    <>
      <JourneyTitle title={"This link can't be used"} text={text} />
      {sent ? (
        <div
          className={
            "rounded-lg border border-[#D6EFE0] bg-[#F6FBF8] px-3 py-2.5 text-sm text-[#1E7F4A]"
          }>
          {sent}
        </div>
      ) : (
        <>
          <label htmlFor={"setup-email"} className={JOURNEY_LABEL}>
            Your email
          </label>
          <input
            id={"setup-email"}
            type={"email"}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && void send()}
            className={JOURNEY_INPUT}
          />
          <button
            type={"button"}
            className={`${JOURNEY_BUTTON} mt-4`}
            disabled={sending || !email.trim()}
            onClick={() => void send()}>
            {sending ? "Sending…" : "Send me a new link"}
          </button>
        </>
      )}
      <div className={"mt-3.5 text-center text-[12.5px] text-[#7A7F8C]"}>
        Already set up?{" "}
        <button
          type={"button"}
          className={"font-bold text-[#6B4EFF]"}
          onClick={() => navigate("/")}>
          Sign in
        </button>
      </div>
    </>
  );
};
